import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  DrawingManager,
} from "@react-google-maps/api";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import FanModal from "./FanModal";
import {
  getKumesById,
  saveKumes,
  getIller,
  getIlceler,
  getBolgelerForKumesForm,
  getVeterinerlerForKumesForm,
  getYemFabrikalariForKumesForm,
  getHayvanCinsleriForKumesForm,
  getKumesCatiTipleri,
  getKlapeTipleri,
  getYakilTipleri,
  getSobaTipleri,
  getSobaKonumlari,
  getPedAdetleri,
  getFanlar,
  deleteFan,
  getOluHayvanImhaTipleri, //, getBakicilar
} from "../services/api";

const IMAGE_BASE_URL = "https://portal.ekumes.com.tr";
const ORANGE_COLOR = "#FF7A2B";
const ORANGE_HOVER_COLOR = "#E66A20";
const MAPS_LIBRARIES = ["drawing"];

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`kumes-tabpanel-${index}`}
      aria-labelledby={`kumes-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

// Yardımcı Bileşen: Başlıklı Kutu
const SectionBox = ({ title, children }) => (
  <Box
    sx={{
      border: `1px solid ${ORANGE_COLOR}`,
      p: 3,
      pt: 4,
      borderRadius: 2,
      position: "relative",
      mt: 2,
    }}
  >
    <Typography
      variant="h6"
      sx={{
        position: "absolute",
        top: "-14px",
        left: "20px",
        backgroundColor: "background.paper",
        px: 1,
        color: ORANGE_COLOR,
        fontWeight: "bold",
      }}
    >
      {title}
    </Typography>
    <Stack spacing={2}>{children}</Stack>
  </Box>
);

// Ana Bileşen: KumesDuzenle
const KumesDuzenle = ({ kumesId, onSaveSuccess, onCancel }) => {
  const [aktifSekme, setAktifSekme] = useState(0);
  const [formData, setFormData] = useState({});
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  // Dropdown state'leri
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [bolgeler, setBolgeler] = useState([]);
  const [veterinerler, setVeterinerler] = useState([]);
  const [yemFabrikalari, setYemFabrikalari] = useState([]);
  const [hayvanCinsleri, setHayvanCinsleri] = useState([]);
  const [catiTipleri, setCatiTipleri] = useState([]);
  const [klapeTipleri, setKlapeTipleri] = useState([]);
  const [yakitTipleri, setYakitTipleri] = useState([]);
  const [sobaTipleri, setSobaTipleri] = useState([]);
  const [sobaKonumlari, setSobaKonumlari] = useState([]);
  const [pedAdetleri, setPedAdetleri] = useState([]);
  const [imhaTipleri, setImhaTipleri] = useState([]);

  const [fanlar, setFanlar] = useState([]);
  const [bakicilar, setBakicilar] = useState([]);

  const [isFanModalOpen, setFanModalOpen] = useState(false);
  const [editingFanId, setEditingFanId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!kumesId) return;
    try {
      setYukleniyor(true);
      setHata("");

      const kumesResult = await getKumesById(kumesId);
      const kumesData = kumesResult?.data?.data?.[0] || {};
      setFormData(kumesData);

      const sirketId = kumesData.ENTEGRE_SIRKET_ID;
      if (!sirketId) {
        enqueueSnackbar("Kümese ait şirket bilgisi bulunamadı!", {
          variant: "error",
        });
        setYukleniyor(false);
        return;
      }

      const apiCalls = [
        getIller(),
        getBolgelerForKumesForm(sirketId),
        getVeterinerlerForKumesForm(sirketId),
        getYemFabrikalariForKumesForm(sirketId),
        getHayvanCinsleriForKumesForm(),
        getKumesCatiTipleri(),
        getKlapeTipleri(),
        getYakilTipleri(),
        getSobaTipleri(),
        getSobaKonumlari(),
        getPedAdetleri(),
        getOluHayvanImhaTipleri(),
        getFanlar(kumesId), //getBakicilar(kumesId)
      ];

      const results = await Promise.allSettled(apiCalls);
      const checkResult = (result, name) => {
        if (result.status === "rejected") {
          enqueueSnackbar(`${name} verileri yüklenirken hata oluştu!`, {
            variant: "warning",
          });
          return [];
        }
        return result.value?.data?.data || [];
      };

      setIller(checkResult(results[0], "İller"));
      setBolgeler(checkResult(results[1], "Bölgeler"));
      setVeterinerler(checkResult(results[2], "Veterinerler"));
      setYemFabrikalari(checkResult(results[3], "Yem Fabrikaları"));
      setHayvanCinsleri(checkResult(results[4], "Hayvan Cinsleri"));
      setCatiTipleri(checkResult(results[5], "Çatı Tipleri"));
      setKlapeTipleri(checkResult(results[6], "Klape Tipleri"));
      setYakitTipleri(checkResult(results[7], "Yakıt Tipleri"));
      setSobaTipleri(checkResult(results[8], "Soba Tipleri"));
      setSobaKonumlari(checkResult(results[9], "Soba Konumları"));
      setPedAdetleri(checkResult(results[10], "Ped Adetleri"));
      setImhaTipleri(checkResult(results[11], "İmha Tipleri"));
      setFanlar(checkResult(results[12], "Fanlar"));
      setBakicilar(checkResult(results[13], "Bakıcılar"));

      if (kumesData.IL_ID) {
        const ilceRes = await getIlceler(kumesData.IL_ID);
        setIlceler(ilceRes.data.data || []);
      }
    } catch (err) {
      setHata("Veriler yüklenirken bir hata oluştu.");
      enqueueSnackbar(`Hata: ${err.message}`, { variant: "error" });
    } finally {
      setYukleniyor(false);
    }
  }, [kumesId, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSekmeDegistir = (event, newValue) => setAktifSekme(newValue);
  const handleInputChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleIlChange = async (e) => {
    const ilId = e.target.value;
    setFormData((prev) => ({ ...prev, IL_ID: ilId, ILCE_ID: "" }));
    setIlceler([]);
    if (ilId) {
      try {
        const res = await getIlceler(ilId);
        setIlceler(res.data.data || []);
      } catch (err) {
        enqueueSnackbar("İlçeler getirilirken hata oluştu.", {
          variant: "error",
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (!formData.KUMES_ADI || !formData.IL_ID || !formData.ILCE_ID) {
      enqueueSnackbar("Kümes adı, il ve ilçe alanları zorunludur.", {
        variant: "warning",
      });
      return;
    }

    setYukleniyor(true);
    try {
      const payload = { ...formData };
      Object.keys(payload).forEach((key) => {
        if (
          (key.endsWith("_ADI") && key !== "KUMES_ADI") ||
          key.endsWith("_ADI_SOYADI") ||
          key.endsWith("_TANIM_ADI")
        ) {
          delete payload[key];
        }
        if (payload[key] === null) {
          payload[key] = "";
        }
      });

      await saveKumes(kumesId, payload);
      enqueueSnackbar("Kümes bilgileri başarıyla güncellendi!", {
        variant: "success",
      });
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data ||
        "Bilinmeyen bir hata oluştu.";
      enqueueSnackbar(`Güncelleme sırasında hata: ${errorMessage}`, {
        variant: "error",
      });
    } finally {
      setYukleniyor(false);
    }
  };

  const refetchFanlar = useCallback(async () => {
    if (kumesId) {
      try {
        const res = await getFanlar(kumesId);
        setFanlar(res.data.data || []);
      } catch (error) {
        enqueueSnackbar("Fanlar yeniden yüklenirken bir hata oluştu.", {
          variant: "warning",
        });
      }
    }
  }, [kumesId, enqueueSnackbar]);

  const handleOpenFanModal = (fanId = null) => {
    setEditingFanId(fanId);
    setFanModalOpen(true);
  };
  const handleCloseFanModal = () => {
    setEditingFanId(null);
    setFanModalOpen(false);
  };
  const handleFanSave = () => {
    refetchFanlar();
  };

  const handleDeleteFan = async (fanId) => {
    if (window.confirm("Bu fanı silmek istediğinizden emin misiniz?")) {
      try {
        await deleteFan(fanId);
        enqueueSnackbar("Fan başarıyla silindi.", { variant: "success" });
        refetchFanlar();
      } catch (err) {
        enqueueSnackbar(`Silme işlemi sırasında hata: ${err.message}`, {
          variant: "error",
        });
      }
    }
  };

  if (yukleniyor)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress sx={{ color: ORANGE_COLOR }} />
      </Box>
    );
  if (hata) return <Alert severity="error">{hata}</Alert>;

  return (
    <>
      <Paper sx={{ p: 2, margin: 2, borderRadius: 2, boxShadow: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ color: ORANGE_COLOR }}>
          Kümes Düzenle: <strong>{formData.KUMES_ADI || "..."}</strong>
        </Typography>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={aktifSekme}
            onChange={handleSekmeDegistir}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .Mui-selected": { color: ORANGE_COLOR, fontWeight: "bold" },
              "& .MuiTabs-indicator": { backgroundColor: ORANGE_COLOR },
            }}
          >
            <Tab label="Kümes Bilgileri" />
            <Tab label="Fiziksel Özellikler" />
            <Tab label="Fanlar" />
            <Tab label="Klapeler" />
            <Tab label="Isıtma Sistemi" />
            <Tab label="Ped Sistemi" />
            <Tab label="Üretim Bilgileri" />
            <Tab label="Bakıcılar" />
            <Tab label="Harita Konumu" />
          </Tabs>
        </Box>

        <TabPanel value={aktifSekme} index={0}>
          <KumesBilgileriTab
            {...{ formData, handleInputChange, handleIlChange, iller, ilceler }}
          />
        </TabPanel>
        <TabPanel value={aktifSekme} index={1}>
          <FizikselOzelliklerTab
            {...{ formData, handleInputChange, catiTipleri }}
          />
        </TabPanel>
        <TabPanel value={aktifSekme} index={2}>
          <FanlarTab
            fanlar={fanlar}
            onAdd={() => handleOpenFanModal()}
            onEdit={handleOpenFanModal}
            onDelete={handleDeleteFan}
          />
        </TabPanel>
        <TabPanel value={aktifSekme} index={3}>
          <KlapelerTab {...{ formData, handleInputChange, klapeTipleri }} />
        </TabPanel>
        <TabPanel value={aktifSekme} index={4}>
          <IsitmaTab
            {...{
              formData,
              handleInputChange,
              yakitTipleri,
              sobaTipleri,
              sobaKonumlari,
            }}
          />
        </TabPanel>
        <TabPanel value={aktifSekme} index={5}>
          <PedlerTab {...{ formData, handleInputChange, pedAdetleri }} />
        </TabPanel>
        <TabPanel value={aktifSekme} index={6}>
          <UretimBilgileriTab
            {...{
              formData,
              setFormData,
              handleInputChange,
              bolgeler,
              veterinerler,
              yemFabrikalari,
              hayvanCinsleri,
              imhaTipleri,
            }}
          />
        </TabPanel>
        <TabPanel value={aktifSekme} index={7}>
          <BakicilarTab bakicilar={bakicilar} />
        </TabPanel>
        <TabPanel value={aktifSekme} index={8}>
          <HaritaTab
            latitude={parseFloat(formData.GPRS_LAT)}
            longitude={parseFloat(formData.GPRS_LONG)}
            setFormData={setFormData}
          />
        </TabPanel>

        <Box
          sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{
              borderColor: ORANGE_COLOR,
              color: ORANGE_COLOR,
              "&:hover": { borderColor: ORANGE_HOVER_COLOR },
            }}
          >
            Geri Dön
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={yukleniyor}
            sx={{
              backgroundColor: ORANGE_COLOR,
              "&:hover": { backgroundColor: ORANGE_HOVER_COLOR },
            }}
          >
            Kaydet
          </Button>
        </Box>
      </Paper>

      {isFanModalOpen && (
        <FanModal
          open={isFanModalOpen}
          onClose={handleCloseFanModal}
          onSave={handleFanSave}
          kumesId={kumesId}
          fanId={editingFanId}
        />
      )}
    </>
  );
};

const KumesBilgileriTab = ({
  formData,
  handleInputChange,
  handleIlChange,
  iller,
  ilceler,
}) => (
  <SectionBox title="Kümes Genel Bilgileri">
    <TextField
      fullWidth
      label="Kümes Adı"
      name="KUMES_ADI"
      value={formData.KUMES_ADI || ""}
      onChange={handleInputChange}
      required
    />
    <TextField
      fullWidth
      multiline
      rows={3}
      label="Adres"
      name="ADRES"
      value={formData.ADRES || ""}
      onChange={handleInputChange}
    />
    <FormControl fullWidth required>
      <InputLabel>İl</InputLabel>
      <Select
        name="IL_ID"
        value={formData.IL_ID || ""}
        label="İl"
        onChange={handleIlChange}
      >
        {iller.map((il) => (
          <MenuItem key={il.IL_ID} value={il.IL_ID}>
            {il.IL_ADI}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <FormControl fullWidth required disabled={!formData.IL_ID}>
      <InputLabel>İlçe</InputLabel>
      <Select
        name="ILCE_ID"
        value={formData.ILCE_ID || ""}
        label="İlçe"
        onChange={handleInputChange}
      >
        {ilceler.map((ilce) => (
          <MenuItem key={ilce.ILCE_ID} value={ilce.ILCE_ID}>
            {ilce.ILCE_ADI}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </SectionBox>
);

const FizikselOzelliklerTab = ({
  formData,
  handleInputChange,
  catiTipleri,
}) => {
  const seciliCati = catiTipleri.find(
    (c) => c.KUMES_CATI_TIP_ID === formData.KUMES_CATI_TIP_ID
  );
  const relativePath = seciliCati?.RESIM_YOLU;
  const fullImageUrl = relativePath ? `${IMAGE_BASE_URL}${relativePath}` : "";

  return (
    <SectionBox title="Fiziksel Özellikler">
      <TextField
        fullWidth
        type="number"
        label="Kümes Eni (m)"
        name="KUMES_EN_METRE"
        value={formData.KUMES_EN_METRE || ""}
        onChange={handleInputChange}
      />
      <TextField
        fullWidth
        type="number"
        label="Kümes Boyu (m)"
        name="KUMES_BOY_METRE"
        value={formData.KUMES_BOY_METRE || ""}
        onChange={handleInputChange}
      />
      <FormControl fullWidth>
        <InputLabel>Kümes Çatı Tipi</InputLabel>
        <Select
          name="KUMES_CATI_TIP_ID"
          value={formData.KUMES_CATI_TIP_ID || ""}
          label="Kümes Çatı Tipi"
          onChange={handleInputChange}
        >
          {catiTipleri.map((tip) => (
            <MenuItem key={tip.KUMES_CATI_TIP_ID} value={tip.KUMES_CATI_TIP_ID}>
              {tip.KUMES_CATI_TIP_ADI}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {fullImageUrl && (
        <Box sx={{ alignSelf: "center", mt: 2 }}>
          <img
            src={fullImageUrl}
            alt={seciliCati?.KUMES_CATI_TIP_ADI || ""}
            style={{
              maxWidth: "100%",
              height: "auto",
              maxHeight: "200px",
              borderRadius: "8px",
            }}
          />
        </Box>
      )}
    </SectionBox>
  );
};

const FanlarTab = ({ fanlar, onAdd, onEdit, onDelete }) => (
  <SectionBox title="Fan Bilgileri">
    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: -1 }}>
      <Button
        onClick={onAdd}
        variant="outlined"
        startIcon={<FaPlus />}
        sx={{
          borderColor: ORANGE_COLOR,
          color: ORANGE_COLOR,
          "&:hover": { borderColor: ORANGE_HOVER_COLOR },
        }}
      >
        Yeni Fan Ekle
      </Button>
    </Box>
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fan No</TableCell>
            <TableCell>Konum</TableCell>
            <TableCell>Boyut</TableCell>
            <TableCell>Güç</TableCell>
            <TableCell align="right">İşlemler</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fanlar.length > 0 ? (
            fanlar.map((fan) => (
              <TableRow key={fan.KUMES_FAN_ID}>
                <TableCell>{fan.FAN_NO}</TableCell>
                <TableCell>{fan.KONUM_ADI}</TableCell>
                <TableCell>{fan.BOYUT_ADI}</TableCell>
                <TableCell>{fan.GUC_ADI}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    sx={{ color: ORANGE_COLOR }}
                    onClick={() => onEdit(fan.KUMES_FAN_ID)}
                  >
                    <FaEdit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(fan.KUMES_FAN_ID)}
                  >
                    <FaTrash />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                Kayıtlı fan bulunamadı.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </SectionBox>
);

const KlapelerTab = ({ formData, handleInputChange, klapeTipleri }) => (
  <SectionBox title="Klape Bilgileri">
    <FormControl fullWidth>
      <InputLabel>Klape Tipi</InputLabel>
      <Select
        name="KLAPE_TIP_ID"
        value={formData.KLAPE_TIP_ID || ""}
        label="Klape Tipi"
        onChange={handleInputChange}
      >
        {klapeTipleri.map((tip) => (
          <MenuItem key={tip.KLAPE_TIP_ID} value={tip.KLAPE_TIP_ID}>
            {tip.KLAPE_TIP_ADI}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <TextField
      fullWidth
      type="number"
      label="Klape Sayısı"
      name="KLAPE_SAYISI"
      value={formData.KLAPE_SAYISI || ""}
      onChange={handleInputChange}
    />
  </SectionBox>
);

const IsitmaTab = ({
  formData,
  handleInputChange,
  yakitTipleri,
  sobaTipleri,
  sobaKonumlari,
}) => (
  <SectionBox title="Isıtma Sistemi Bilgileri">
    <FormControl fullWidth>
      <InputLabel>Yakıt Tipi</InputLabel>
      <Select
        name="YAKIT_TIP_ID"
        value={formData.YAKIT_TIP_ID || ""}
        label="Yakıt Tipi"
        onChange={handleInputChange}
      >
        {yakitTipleri.map((tip) => (
          <MenuItem key={tip.YAKIT_TIP_ID} value={tip.YAKIT_TIP_ID}>
            {tip.YAKIT_TIP_ADI}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <FormControl fullWidth>
      <InputLabel>Soba Tipi</InputLabel>
      <Select
        name="SOBA_TIP_ID"
        value={formData.SOBA_TIP_ID || ""}
        label="Soba Tipi"
        onChange={handleInputChange}
      >
        {sobaTipleri.map((tip) => (
          <MenuItem key={tip.SOBA_TIP_ID} value={tip.SOBA_TIP_ID}>
            {tip.SOBA_TIP_ADI}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <FormControl fullWidth>
      <InputLabel>Soba Konumu</InputLabel>
      <Select
        name="SOBA_YER_TIP_ID"
        value={formData.SOBA_YER_TIP_ID || ""}
        label="Soba Konumu"
        onChange={handleInputChange}
      >
        {sobaKonumlari.map((tip) => (
          <MenuItem key={tip.SOBA_YER_TIP_ID} value={tip.SOBA_YER_TIP_ID}>
            {tip.SOBA_YER_TIP_ADI}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </SectionBox>
);

const PedlerTab = ({ formData, handleInputChange, pedAdetleri }) => (
  <SectionBox title="Ped Sistemi Bilgileri">
    <FormControl fullWidth>
      <InputLabel>Ped Adeti</InputLabel>
      <Select
        name="PED_ADET_ID"
        value={formData.PED_ADET_ID || ""}
        label="Ped Adeti"
        onChange={handleInputChange}
      >
        {pedAdetleri.map((tip) => (
          <MenuItem key={tip.PED_ADET_ID} value={tip.PED_ADET_ID}>
            {tip.PED_ADET_ADI}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <TextField
      fullWidth
      type="number"
      label="Ped En (cm)"
      name="PED_EN_CM"
      value={formData.PED_EN_CM || ""}
      onChange={handleInputChange}
    />
    <TextField
      fullWidth
      type="number"
      label="Ped Boy (cm)"
      name="PED_BOY_CM"
      value={formData.PED_BOY_CM || ""}
      onChange={handleInputChange}
    />
  </SectionBox>
);

const UretimBilgileriTab = ({
  formData,
  setFormData,
  handleInputChange,
  bolgeler,
  veterinerler,
  yemFabrikalari,
  hayvanCinsleri,
  imhaTipleri,
}) => {
  const handleImhaTipiChange = (e) => {
    const imhaTipId = e.target.value;
    const secilenTip = imhaTipleri.find(
      (tip) => tip.OLU_HAYVAN_IMHA_TIP_ID === imhaTipId
    );
    let mesafe = "";
    if (secilenTip && secilenTip.IMHA_MESAFESI_METRE > 0) {
      mesafe = secilenTip.IMHA_MESAFESI_METRE;
    }
    setFormData((prev) => ({
      ...prev,
      OLU_HAYVAN_IMHA_TIP_ID: imhaTipId,
      IMHA_MESAFESI_METRE: mesafe,
    }));
  };

  const isMesafeDisabled = () => {
    const secilenTip = imhaTipleri.find(
      (tip) => tip.OLU_HAYVAN_IMHA_TIP_ID === formData.OLU_HAYVAN_IMHA_TIP_ID
    );
    return secilenTip && secilenTip.IMHA_MESAFESI_METRE > 0;
  };

  return (
    <SectionBox title="Üretim Bilgileri">
      <FormControl fullWidth>
        <InputLabel>Bölge</InputLabel>
        <Select
          name="BOLGE_ID"
          value={formData.BOLGE_ID || ""}
          label="Bölge"
          onChange={handleInputChange}
        >
          {bolgeler.map((b) => (
            <MenuItem key={b.BOLGE_ID} value={b.BOLGE_ID}>
              {b.BOLGE_ADI}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Veteriner</InputLabel>
        <Select
          name="VETERINER_ID"
          value={formData.VETERINER_ID || ""}
          label="Veteriner"
          onChange={handleInputChange}
        >
          {veterinerler.map((v) => (
            <MenuItem key={v.VETERINER_ID} value={v.VETERINER_ID}>
              {v.VETERINER_ADI}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Yem Fabrikası</InputLabel>
        <Select
          name="YEM_FABRIKA_ID"
          value={formData.YEM_FABRIKA_ID || ""}
          label="Yem Fabrikası"
          onChange={handleInputChange}
        >
          {yemFabrikalari.map((y) => (
            <MenuItem key={y.YEM_ID} value={y.YEM_ID}>
              {y.YEM_FABRIKA_ADI}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Hayvan Cinsi</InputLabel>
        <Select
          name="HAYVAN_CINS_ID"
          value={formData.HAYVAN_CINS_ID || ""}
          label="Hayvan Cinsi"
          onChange={handleInputChange}
        >
          {hayvanCinsleri.map((h) => (
            <MenuItem key={h.HAYVAN_CINS_ID} value={h.HAYVAN_CINS_ID}>
              {h.HAYVAN_TANIM_ADI}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Ölen Hayvan İmha Tipi</InputLabel>
        <Select
          name="OLU_HAYVAN_IMHA_TIP_ID"
          value={formData.OLU_HAYVAN_IMHA_TIP_ID || ""}
          label="Ölen Hayvan İmha Tipi"
          onChange={handleImhaTipiChange}
        >
          {imhaTipleri.map((t) => (
            <MenuItem
              key={t.OLU_HAYVAN_IMHA_TIP_ID}
              value={t.OLU_HAYVAN_IMHA_TIP_ID}
            >
              {t.OLU_HAYVAN_IMHA_TIP_ADI}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        fullWidth
        label="İmha Noktası Kümes Mesafesi (m)"
        name="IMHA_MESAFESI_METRE"
        value={formData.IMHA_MESAFESI_METRE || ""}
        onChange={handleInputChange}
        type="number"
        disabled={isMesafeDisabled()}
      />
    </SectionBox>
  );
};

// KumesDuzenle.js dosyasının en altındaki BakicilarTab bileşeni

const BakicilarTab = ({ bakicilar }) => {
  const isBakiciAktif = (bakici) => {
    // Eğer bitiş tarihi yoksa veya bitiş tarihi bugünden sonraysa, bakıcı aktiftir.
    if (!bakici.BITIS_TARIHI) {
      return true;
    }
    try {
      const bugun = new Date();
      const bitisTarihi = new Date(
        bakici.BITIS_TARIHI.year,
        bakici.BITIS_TARIHI.month - 1,
        bakici.BITIS_TARIHI.day
      );

      bitisTarihi.setHours(23, 59, 59, 999);

      return bitisTarihi >= bugun;
    } catch (error) {
      return false;
    }
  };

  return (
    <SectionBox title="Bakıcı Bilgileri">
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Adı Soyadı</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Durum</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bakicilar && bakicilar.length > 0 ? (
              bakicilar.map((bakici) => {
                const aktifMi = isBakiciAktif(bakici);
                return (
                  <TableRow
                    key={bakici.KUMESLER_DONEM_BAKICI_ID || bakici.BAKICI_ID}
                  >
                    <TableCell>{`${bakici.BAKICI_ADI} ${bakici.BAKICI_SOYADI}`}</TableCell>

                    <TableCell>{bakici.BAKICI_TEL}</TableCell>

                    <TableCell>
                      <Chip
                        label={aktifMi ? "Aktif" : "Pasif"}
                        color={aktifMi ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Kayıtlı bakıcı bulunamadı.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SectionBox>
  );
};
const HaritaTab = ({ latitude, longitude, setFormData }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_Maps_API_KEY,
    libraries: MAPS_LIBRARIES,
  });

  const [center, setCenter] = useState({ lat: 40.74, lng: 31.57 });

  useEffect(() => {
    if (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) {
      setCenter({ lat: latitude, lng: longitude });
    }
  }, [latitude, longitude]);

  const handleMapClick = useCallback(
    (event) => {
      const newLat = event.latLng.lat();
      const newLng = event.latLng.lng();
      setCenter({ lat: newLat, lng: newLng });
      setFormData((prev) => ({ ...prev, GPRS_LAT: newLat, GPRS_LONG: newLng }));
    },
    [setFormData]
  );

  const onPolygonComplete = useCallback((polygon) => {
    polygon.setMap(null);
  }, []);

  if (loadError)
    return (
      <Alert severity="error">
        Harita yüklenemedi. API anahtarınızı kontrol edin.
      </Alert>
    );
  if (!isLoaded)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
        <CircularProgress sx={{ color: ORANGE_COLOR }} />
      </Box>
    );

  return (
    <SectionBox title="Kümes Konumu">
      <Typography variant="caption">
        Haritada yeni bir konuma tıklayarak veya işaretçiyi sürükleyerek kümesin
        yerini güncelleyebilirsiniz.
      </Typography>
      <Box
        sx={{
          height: "450px",
          width: "100%",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={center}
          zoom={15}
          onClick={handleMapClick}
        >
          <Marker
            position={center}
            draggable={true}
            onDragEnd={handleMapClick}
          />
          <DrawingManager
            onPolygonComplete={onPolygonComplete}
            options={{
              drawingControl: true,
              drawingControlOptions: {
                position: window.google.maps.ControlPosition.TOP_CENTER,
                drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
              },
              polygonOptions: {
                fillColor: ORANGE_COLOR,
                fillOpacity: 0.3,
                strokeColor: ORANGE_COLOR,
                strokeWeight: 2,
                clickable: false,
                editable: true,
                zIndex: 1,
              },
            }}
          />
        </GoogleMap>
      </Box>
    </SectionBox>
  );
};

export default KumesDuzenle;
